import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { AppWorkOrdersWorkspace } from '../AppWorkOrdersWorkspace';
import { getBillingQueue, reportRejectReason } from '../data/model';
import type { FlowState, WorkOrder } from '../data/types';
import { formatHumanDateTimeLabel } from '../utils';

type OrderFilter = 'all' | 'en-cours' | 'a-valider' | 'facturees';

function matchesFilter(order: WorkOrder, filter: OrderFilter): boolean {
  const s = order.status.toLowerCase();
  switch (filter) {
    case 'en-cours': return s.includes('en cours') || s.includes('sur site') || s.includes('en route');
    case 'a-valider': return s.includes('valid') || s.includes('soumis');
    case 'facturees': return s.includes('factur');
    default: return true;
  }
}

export function WorkOrdersPage(props: {
  orders: WorkOrder[];
  selectedOrderId: string;
  flowState: FlowState;
  onSelectOrder: (id: string) => void;
  onFocusScenario: () => void;
  onReturnDispatch: () => void;
  onValidateReport: () => void;
  onRejectReport: () => void;
  onOpenReworkMobile: () => void;
  onCreateInvoice: () => void;
  onProposeFollowUp: () => void;
  onOpenWorkOrderById: (workOrderId: string) => void;
  appWorkspaceEnabled: boolean;
  currentUserRole: string | null;
  canReviewActions: boolean;
  canOpenReworkMobile: boolean;
  canCreateInvoice: boolean;
  canProposeFollowUp: boolean;
}) {
  const [activeFilter, setActiveFilter] = useState<OrderFilter>('all');
  const [checkedChecklist, setCheckedChecklist] = useState<Record<string, boolean>>({});
  const [selectedProof, setSelectedProof] = useState<string | null>(null);
  const selectedOrder = props.orders.find((item) => item.id === props.selectedOrderId) ?? props.orders[0];
  const canReview = ['submitted', 'resubmitted'].includes(props.flowState.reportStatus);
  const billingQueue = getBillingQueue(selectedOrder);
  const filteredOrders = props.orders.filter((order) => matchesFilter(order, activeFilter));
  const filterCounts = {
    all: props.orders.length,
    'en-cours': props.orders.filter((o) => matchesFilter(o, 'en-cours')).length,
    'a-valider': props.orders.filter((o) => matchesFilter(o, 'a-valider')).length,
    'facturees': props.orders.filter((o) => matchesFilter(o, 'facturees')).length,
  };

  const showInvoiceButton = props.flowState.reportStatus === 'validated' || props.flowState.invoiceCreated;
  const secondaryLabel = props.flowState.invoiceCreated
    ? 'Facture créée'
    : props.flowState.reportStatus === 'validated'
      ? 'Créer facture'
      : props.flowState.reportStatus === 'rejected'
        ? 'Reprise demandée'
        : 'Demander reprise';
  const primaryDisabled = props.flowState.reportStatus === 'rejected'
    ? !props.canOpenReworkMobile
    : props.flowState.reportStatus === 'validated' || !props.canReviewActions || !canReview;
  const secondaryDisabled = showInvoiceButton
    ? props.flowState.invoiceCreated || !props.canCreateInvoice
    : props.flowState.reportStatus === 'rejected' || props.flowState.invoiceCreated || !props.canReviewActions || !canReview;

  useEffect(() => {
    setCheckedChecklist(Object.fromEntries(selectedOrder.checklist.map((item) => [item.label, item.done])));
    setSelectedProof(selectedOrder.proof[0] ?? null);
  }, [selectedOrder.id]);

  return (
    <div className="page-stack">
      <div className="work-order-layout">
        <Card title="Interventions" subtitle="Sélection de dossiers actifs ou en validation.">
          <div className="order-filter-tabs">
            {([
              { key: 'all' as OrderFilter, label: 'Tous' },
              { key: 'en-cours' as OrderFilter, label: 'En cours' },
              { key: 'a-valider' as OrderFilter, label: 'À valider' },
              { key: 'facturees' as OrderFilter, label: 'Facturées' },
            ]).map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`order-filter-tab${activeFilter === tab.key ? ' is-active' : ''}`}
                onClick={() => setActiveFilter(tab.key)}
              >
                {tab.label}
                <span className="order-filter-tab__count">{filterCounts[tab.key]}</span>
              </button>
            ))}
          </div>
          <div className="order-list">
            {filteredOrders.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`order-list__item${item.id === selectedOrder.id ? ' is-active' : ''}`}
                onClick={() => props.onSelectOrder(item.id)}
              >
                <div>
                  <strong>{item.id}</strong>
                  <span>{item.title}</span>
                </div>
                <em>{item.status}</em>
              </button>
            ))}
            {filteredOrders.length === 0 ? (
              <div className="app-empty">
                <span className="material-symbols-outlined" style={{fontSize: '2rem', opacity: 0.4}}>filter_list_off</span>
                <span>Aucune intervention dans ce filtre.</span>
              </div>
            ) : null}
          </div>
        </Card>

        <div className="order-detail">
          <Card title={`${selectedOrder.id} · ${selectedOrder.title}`} subtitle={`${selectedOrder.client} · ${selectedOrder.site}`}>
            <div className="detail-grid detail-grid--summary">
              <div className="detail-card">
                <span className="detail-card__label">Statut</span>
                <strong>{selectedOrder.status}</strong>
                <span>
                  {selectedOrder.invoiceStatus} · {selectedOrder.window}
                </span>
              </div>
              <div className="detail-card">
                <span className="detail-card__label">Responsable</span>
                <strong>{selectedOrder.owner}</strong>
                <span>Technicien principal</span>
              </div>
              <div className="detail-card">
                <span className="detail-card__label">Valeur</span>
                <strong>{selectedOrder.revenue}</strong>
                <span>{selectedOrder.invoiceAction}</span>
              </div>
              <div className="detail-card">
                <span className="detail-card__label">Action suivante</span>
                <strong>{selectedOrder.nextAction}</strong>
                <span>{selectedOrder.upsell}</span>
              </div>
            </div>

            <div className="detail-actions">
              <button
                type="button"
                className="primary-button"
                onClick={props.flowState.reportStatus === 'rejected' ? props.onOpenReworkMobile : props.onValidateReport}
                disabled={primaryDisabled}
              >
                {props.flowState.reportStatus === 'rejected'
                  ? 'Ouvrir la reprise mobile'
                  : props.flowState.reportStatus === 'validated'
                    ? 'Rapport validé'
                    : 'Valider rapport'}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={showInvoiceButton ? props.onCreateInvoice : props.onRejectReport}
                disabled={secondaryDisabled}
              >
                {secondaryLabel}
              </button>
              <button type="button" className="secondary-button" onClick={props.onReturnDispatch}>
                Retour planification
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={props.onProposeFollowUp}
                disabled={!props.canProposeFollowUp}
              >
                Proposer suivi
              </button>
            </div>
          </Card>

          <div className="two-column-layout">
            <Card title="Checklist et chronologie">
              <div className="timeline-panel">
                <ul className="check-list">
                  {selectedOrder.checklist.map((item) => (
                    <li key={item.label} className={checkedChecklist[item.label] ? 'is-done' : ''}>
                      <button
                        type="button"
                        className="check-list__item"
                        onClick={() => setCheckedChecklist((current) => ({ ...current, [item.label]: !current[item.label] }))}
                      >
                        <span className="check-list__box" aria-hidden="true">
                          {checkedChecklist[item.label] ? '✓' : ''}
                        </span>
                        <span>{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="timeline-list">
                  {selectedOrder.timeline.map((item) => (
                    <button
                      key={`${item.at}-${item.label}`}
                      type="button"
                      className="timeline-list__item timeline-list__item--interactive"
                      onClick={() => props.onOpenWorkOrderById(selectedOrder.id)}
                    >
                      <strong>{formatHumanDateTimeLabel(`2026-04-01T${item.at}:00`)}</strong>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            <Card title="Preuves terrain & brouillon vocal">
              <div className="proof-stack">
                <div className="proof-tags">
                  {selectedOrder.proof.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={`proof-tag${selectedProof === item ? ' is-active' : ''}`}
                      onClick={() => setSelectedProof(item)}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                {selectedProof ? (
                  <div className="proof-preview">
                    <strong>{selectedProof}</strong>
                    <span>Ouvre la preuve ou l’aperçu associé quand l’intégration média sera disponible.</span>
                  </div>
                ) : null}
                {props.flowState.reportStatus === 'rejected' ? (
                  <div className="alert-card alert-card--warning">
                    <strong>Motif de refus</strong>
                    <span>{props.flowState.rejectionReason ?? reportRejectReason}</span>
                  </div>
                ) : null}
                <div className="voice-draft">
                  <span className="voice-draft__label">Résumé vocal</span>
                  <p>{selectedOrder.voiceDraft}</p>
                </div>
              </div>
            </Card>
          </div>

          <Card
            title="File de validation et facturation"
            subtitle="La zone qui transforme la preuve terrain en trésorerie visible."
          >
            <div className="billing-list">
              {billingQueue.map((item) => (
                <button
                  key={item.title}
                  type="button"
                  className={`billing-item billing-item--interactive${item.tone ? ` billing-item--${item.tone}` : ''}`}
                  onClick={() => props.onOpenWorkOrderById(selectedOrder.id)}
                >
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                  <div className="billing-item__meta">
                    <span>{item.amount}</span>
                    <span>{item.status}</span>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <AppWorkOrdersWorkspace enabled={props.appWorkspaceEnabled} currentUserRole={props.currentUserRole ?? null} />
    </div>
  );
}
